import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { trackEvent, trackPageView } from '../utils/analytics';
import { trackVisit } from '../utils/trackVisit';

/** Mount once inside <BrowserRouter>. Fires a page_view on each route change. */
export const PageViewTracker: React.FC = () => {
  const location = useLocation();
  const lastPath = useRef<string>('');

  useEffect(() => {
    const path = location.pathname + location.search;
    if (path === lastPath.current) return;
    lastPath.current = path;
    trackPageView(path);  // consent-gated YM/GA analytics
    trackVisit(path);     // first-party admin ntfy beacon (always on)
  }, [location.pathname, location.search]);

  return null;
};

interface MoveTrackerOptions {
  /** Flush window in milliseconds. Burst events are batched within this window. */
  windowMs?: number;
  /** Tag added to event params (e.g. point id). */
  pointId?: number | string;
}

/**
 * Lightweight aggregator for high-frequency move/jump events.
 *
 *   const moves = useMoveAggregator({ pointId });
 *   moves.recordMove();   // call from move handler
 *
 * Emits:
 *   - `game_first_move`  on the very first move of the session
 *   - `game_move_burst` { count } every windowMs (default 60s) while moving
 *   - `game_move_total` { count } on unmount with the lifetime total
 */
export const useMoveAggregator = ({ windowMs = 60_000, pointId }: MoveTrackerOptions = {}) => {
  const totalRef     = useRef(0);
  const bucketRef    = useRef(0);
  const firstSentRef = useRef(false);
  const timerRef     = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      if (bucketRef.current === 0) return;
      trackEvent('game_move_burst', { count: bucketRef.current, point_id: pointId });
      bucketRef.current = 0;
    }, windowMs);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      if (totalRef.current > 0) {
        trackEvent('game_move_total', { count: totalRef.current, point_id: pointId });
      }
      totalRef.current = 0;
      bucketRef.current = 0;
      firstSentRef.current = false;
    };
  }, [windowMs, pointId]);

  const recordMove = () => {
    totalRef.current  += 1;
    bucketRef.current += 1;
    if (!firstSentRef.current) {
      firstSentRef.current = true;
      trackEvent('game_first_move', { point_id: pointId });
    }
  };

  return { recordMove };
};
